// Analytics calculations: each subject's day limited to the time window (offsets from that game's
// start), reduced to the numbers the table, headline numbers and findings use, and the chart lines.
// A "game" in the compare bar is one game or a series average (several dates averaged).
import React from 'react';
import { bucketSeries } from '../Retail/overview/overviewMetrics';
import { clockToMinute, minuteToClock } from '../Gates/gatesMetrics';
import { QUEUE_TIME_GOOD_MAX } from '../Concession/concessionTheme';
import { formatDuration } from '../Concession/live/liveMetrics';

export { minuteToClock };

const mean = (values) => {
  const v = values.filter((x) => typeof x === 'number' && !Number.isNaN(x));
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
};

// Seconds -> "45s", "2m 10s"
export const formatSeconds = (seconds) => {
  if (typeof seconds !== 'number' || Number.isNaN(seconds) || seconds < 0) return '—';
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
};

const round5 = (m) => Math.round(m / 5) * 5;
export const relativeToStart = (minutes) => {
  if (minutes == null) return null;
  const m = round5(Math.abs(minutes));
  if (m < 5) return 'right at game start';
  const text = m >= 60 ? formatDuration(m) : `${m} min`;
  return minutes < 0 ? `${text} before game start` : `${text} after game start`;
};

// Minutes of the game day covered by the time window, or null (whole day) when the start is unknown
export const windowRange = (gameDate, window) => {
  const start = clockToMinute(gameDate?.gameStartTime);
  if (start == null) return { startMinute: null, range: null };
  return { startMinute: start, range: { start: start + window.startOffset, end: start + window.endOffset } };
};

const within = (points, range) => (range ? (points || []).filter((p) => p.m >= range.start && p.m < range.end) : (points || []));

const peakOf = (points) => points.reduce((best, p) => (!best || p.v > best.v ? p : best), null);

// Busiest 15 minutes on fixed quarter-hour slots
const busiestQuarter = (points) => {
  const slots = new Map();
  points.forEach(({ m, v }) => {
    const s = Math.floor(m / 15) * 15;
    slots.set(s, (slots.get(s) || 0) + v);
  });
  let best = null;
  slots.forEach((total, start) => { if (total > 0 && (!best || total > best.total)) best = { start, total }; });
  return best;
};

// One subject, one date: { queue, transaction, count, occupancy, entries, exits } summaries (null when no data)
export const summarizeDay = (series, gameDate, window, date) => {
  if (!series) return null;
  const { startMinute, range } = windowRange(gameDate, window);
  const offset = (m) => (startMinute != null && m != null ? m - startMinute : null);
  const out = { date, startMinute, windowStart: range ? range.start : null, windowEnd: range ? range.end : null };

  const readings = (key) => {
    const pts = within(series[key], range);
    if (!pts.length) return null;
    const peak = peakOf(pts);
    return { avg: mean(pts.map((p) => p.v)), peak: { value: peak.v, minute: peak.m, offset: offset(peak.m), date } };
  };
  const q = readings('queue');
  out.queue = q ? { ...q, peak: { ...q.peak, value: Math.round(q.peak.value) }, longMinutes: within(series.queue, range).filter((p) => Math.round(p.v) >= QUEUE_TIME_GOOD_MAX).length } : null;
  out.transaction = readings('transaction');
  out.count = readings('count');
  out.occupancy = readings('occupancy');

  const totals = (key) => {
    if (!series[key]) return null;
    const pts = within(series[key], range);
    if (!pts.length) return null;
    const rush = busiestQuarter(pts);
    return {
      total: pts.reduce((s, p) => s + p.v, 0),
      rush: rush ? { ...rush, offset: offset(rush.start), date } : null,
    };
  };
  out.entries = totals('entries');
  out.exits = totals('exits');
  out.hasData = ['queue', 'transaction', 'count', 'occupancy', 'entries', 'exits'].some((k) => out[k]);
  return out;
};

// Several days (a series average, or the average across games) -> one summary: averages and totals
// are per game, averaged; peaks are the highest of the days (and remember which day)
export const combineDays = (days) => {
  const list = days.filter((d) => d && d.hasData);
  if (!list.length) return null;
  if (list.length === 1) return list[0];
  const pick = (key) => list.map((d) => d[key]).filter(Boolean);
  const combineReadings = (key, extra = () => ({})) => {
    const xs = pick(key);
    if (!xs.length) return null;
    const peak = xs.reduce((best, x) => (!best || x.peak.value > best.value ? x.peak : best), null);
    return { avg: mean(xs.map((x) => x.avg)), peak, ...extra(xs) };
  };
  const combineTotals = (key) => {
    const xs = pick(key);
    if (!xs.length) return null;
    const rushes = xs.map((x) => x.rush).filter(Boolean);
    return {
      total: mean(xs.map((x) => x.total)),
      rush: rushes.length ? { total: mean(rushes.map((r) => r.total)), offset: mean(rushes.map((r) => r.offset)), start: null, date: null } : null,
    };
  };
  return {
    date: null,
    days: list.length,
    startMinute: null,
    queue: combineReadings('queue', (xs) => ({ longMinutes: mean(xs.map((x) => x.longMinutes)) })),
    transaction: combineReadings('transaction'),
    count: combineReadings('count'),
    occupancy: combineReadings('occupancy'),
    entries: combineTotals('entries'),
    exits: combineTotals('exits'),
    hasData: true,
  };
};

// ---------------------------------------------------------------------------
// Table: which number to show for each subject
// ---------------------------------------------------------------------------
export const TABLE_STATS = [
  { key: 'avgQueue', metric: 'queue', label: 'Avg queue', get: (s) => s.queue?.avg, format: (v) => `${v.toFixed(1)} min`, queueColored: true },
  { key: 'peakQueue', metric: 'queue', label: 'Peak queue', get: (s) => s.queue?.peak?.value, format: (v) => `${Math.round(v)} min`, queueColored: true, at: (s) => s.queue?.peak },
  { key: 'entries', metric: 'entries', label: 'Entries', get: (s) => s.entries?.total, format: (v) => Math.round(v).toLocaleString(), bar: true },
  { key: 'exits', metric: 'exits', label: 'Exits', get: (s) => s.exits?.total, format: (v) => Math.round(v).toLocaleString(), bar: true },
  { key: 'peakOccupancy', metric: 'occupancy', label: 'Peak occupancy', get: (s) => s.occupancy?.peak?.value, format: (v) => Math.round(v).toLocaleString(), at: (s) => s.occupancy?.peak, bar: true },
  { key: 'avgOccupancy', metric: 'occupancy', label: 'Avg occupancy', get: (s) => s.occupancy?.avg, format: (v) => Math.round(v).toLocaleString(), bar: true },
  { key: 'avgCount', metric: 'count', label: 'Avg people count', get: (s) => s.count?.avg, format: (v) => Math.round(v).toLocaleString(), bar: true },
  { key: 'avgTransaction', metric: 'transaction', label: 'Avg transaction', get: (s) => s.transaction?.avg, format: (v) => formatSeconds(v) },
];

// ---------------------------------------------------------------------------
// Chart lines
// ---------------------------------------------------------------------------
const linePoints = (series, metric, { gameDate, window, align, resolution, runningTotal }) => {
  const { startMinute, range } = windowRange(gameDate, window);
  const pts = within(series?.[metric], range);
  if (!pts.length) return [];
  const isFlow = metric === 'entries' || metric === 'exits';
  let points = bucketSeries(pts, { align, startMinute, size: runningTotal ? 1 : resolution, agg: isFlow ? 'sum' : 'avg' });
  if (runningTotal) {
    let sum = 0;
    points = points.map((p) => {
      sum += p.y;
      return { x: p.x, y: sum };
    });
  }
  return points;
};

// Several days lined up by game start, averaged at each x. Readings: days without a point there are
// skipped. Running totals: a day that has started keeps its last total (a game still in progress, or
// one whose data ends earlier, would otherwise drop out of the average and make the line jump).
const averageLines = (lines, carryForward = false) => {
  const xs = Array.from(new Set(lines.flatMap((pts) => pts.map((p) => p.x)))).sort((a, b) => a - b);
  const lookups = lines.map((pts) => new Map(pts.map((p) => [p.x, p.y])));
  const last = lines.map(() => null);
  return xs.map((x) => {
    let sum = 0;
    let n = 0;
    lookups.forEach((map, i) => {
      if (map.has(x)) last[i] = map.get(x);
      const y = map.has(x) ? map.get(x) : carryForward ? last[i] : null;
      if (y != null) {
        sum += y;
        n += 1;
      }
    });
    return { x, y: n ? sum / n : 0 };
  });
};

const roundFor = (metric, y) => (metric === 'queue' ? Math.round(y * 10) / 10 : Math.round(y));

// subjects: [{ subject, color }]; games: [{ key, dates: [gameDate], label, dash, color }];
// seriesFor(subject, date) -> minute series (or null while loading)
export const buildChartLines = ({ subjects, games, metric, seriesFor, window, align, resolution, runningTotal, averageGames }) => {
  const lines = [];
  subjects.forEach(({ subject, color }) => {
    if (!subject.metrics.includes(metric)) return;
    const pointsForDate = (gameDate, alignAs) => {
      const series = seriesFor(subject, gameDate.date);
      return series ? linePoints(series, metric, { gameDate, window, align: alignAs, resolution, runningTotal }) : [];
    };
    if (averageGames) {
      const dates = Array.from(new Map(games.flatMap((g) => g.dates).map((d) => [d.date, d])).values());
      const points = averageLines(dates.map((d) => pointsForDate(d, 'start')), runningTotal);
      lines.push({
        key: `${subject.key}-avg`,
        subjectKey: subject.key,
        gameKey: 'avg',
        label: subject.name,
        color,
        dash: [],
        points: points.map((p) => ({ x: p.x, y: roundFor(metric, p.y) })),
      });
      return;
    }
    games.forEach((g) => {
      // A series average is lined up by game start (clock times differ between its games)
      const points = g.dates.length > 1
        ? averageLines(g.dates.map((d) => pointsForDate(d, 'start')), runningTotal)
        : pointsForDate(g.dates[0], align);
      lines.push({
        key: `${subject.key}-${g.key}`,
        subjectKey: subject.key,
        gameKey: g.key,
        label: `${subject.name} · ${g.label.short}`,
        color,
        dash: g.dash,
        gameColor: g.color,
        points: points.map((p) => ({ x: p.x, y: roundFor(metric, p.y) })),
      });
    });
  });
  return lines;
};

// ---------------------------------------------------------------------------
// "What stands out": a few plain findings across the selected locations and games
// rows: [{ subject, color, perGame: [{ game, summary }], overall }]
// ---------------------------------------------------------------------------
export const buildAnalyticsInsights = (rows, games) => {
  const insights = [];
  const withQueue = rows.filter((r) => r.overall?.queue);
  const nGames = games.length;
  // Comparisons between games leave out a game still in progress (its totals are only "so far")
  const finished = games.map((g, i) => ({ g, i })).filter(({ g }) => !g.label.live);

  // Queues at 10 min or more
  const longRows = withQueue
    .map((r) => ({ r, minutes: r.overall.queue.longMinutes || 0 }))
    .filter((x) => x.minutes >= 1)
    .sort((a, b) => b.minutes - a.minutes);
  if (longRows.length) {
    const top = longRows[0];
    const others = longRows.length - 1;
    insights.push({
      key: 'long',
      tone: 'bad',
      icon: 'exclamation-triangle-fill',
      score: 90,
      label: 'Long queues',
      text: (
        <>
          <strong>{top.r.subject.name}</strong> had a queue of {QUEUE_TIME_GOOD_MAX} min or more for
          {' '}<strong>{formatDuration(Math.round(top.minutes))}</strong>{nGames > 1 ? ' per game on average' : ''}
          {others > 0 ? <>, the most of the <strong>{longRows.length}</strong> locations that reached it</> : ''}.
        </>
      ),
    });
  }

  // Longest vs shortest average queue
  if (withQueue.length >= 2) {
    const sorted = [...withQueue].sort((a, b) => b.overall.queue.avg - a.overall.queue.avg);
    const hi = sorted[0];
    const lo = sorted[sorted.length - 1];
    const gap = hi.overall.queue.avg - lo.overall.queue.avg;
    if (gap >= 1) {
      insights.push({
        key: 'gap',
        tone: Math.round(hi.overall.queue.avg) >= QUEUE_TIME_GOOD_MAX ? 'bad' : 'info',
        icon: 'arrow-left-right',
        score: 70 + Math.min(15, gap),
        label: 'Queue gap',
        text: (
          <>
            <strong>{hi.subject.name}</strong> averaged <strong>{hi.overall.queue.avg.toFixed(1)} min</strong>,
            {' '}<strong>{gap.toFixed(1)} min</strong> longer than <strong>{lo.subject.name}</strong> ({lo.overall.queue.avg.toFixed(1)} min).
          </>
        ),
      });
    }
  }

  // Which game had the longest queues across the locations
  if (finished.length >= 2 && withQueue.length) {
    const byGame = finished.map(({ g, i }) => ({
      g,
      avg: mean(withQueue.map((r) => r.perGame[i]?.summary?.queue?.avg)),
    })).filter((x) => x.avg != null).sort((a, b) => b.avg - a.avg);
    if (byGame.length >= 2 && byGame[0].avg - byGame[byGame.length - 1].avg >= 0.5) {
      const top = byGame[0];
      const low = byGame[byGame.length - 1];
      insights.push({
        key: 'game',
        tone: 'info',
        icon: 'calendar-event',
        score: 60,
        label: 'Busiest game',
        text: (
          <>
            Queues were longest on <strong>{top.g.label.long}</strong> ({top.avg.toFixed(1)} min on average
            {withQueue.length > 1 ? ' across the locations' : ''}) and shortest on <strong>{low.g.label.long}</strong> ({low.avg.toFixed(1)} min).
          </>
        ),
      });
    }
  }

  // Busiest location by entries, and when its rush came
  const withEntries = rows.filter((r) => r.overall?.entries?.total > 0).sort((a, b) => b.overall.entries.total - a.overall.entries.total);
  if (withEntries.length) {
    const top = withEntries[0];
    const rush = top.overall.entries.rush;
    const when = rush?.offset != null ? relativeToStart(rush.offset) : null;
    insights.push({
      key: 'entries',
      tone: 'info',
      icon: 'people-fill',
      score: 50,
      label: withEntries.length > 1 ? 'Most entries' : 'Entries',
      text: (
        <>
          <strong>{top.subject.name}</strong> had <strong>{Math.round(top.overall.entries.total).toLocaleString()}</strong> entries
          {nGames > 1 ? ' per game' : ''}
          {when ? <>, busiest {nGames > 1 ? 'typically ' : ''}<strong>{when}</strong></> : ''}.
        </>
      ),
    });
  }

  // Biggest change between games for one location (queue or entries)
  if (finished.length >= 2) {
    let best = null;
    rows.forEach((r) => {
      [['queue', (s) => s?.queue?.avg, (v) => `${v.toFixed(1)} min`, 'average queue'], ['entries', (s) => s?.entries?.total, (v) => Math.round(v).toLocaleString(), 'entries']].forEach(([metric, get, fmt, noun]) => {
        const vals = finished.map(({ i }) => r.perGame[i]).map((pg) => ({ g: pg.game, v: get(pg.summary) })).filter((x) => typeof x.v === 'number');
        if (vals.length < 2) return;
        const hi = vals.reduce((a, b) => (b.v > a.v ? b : a));
        const lo = vals.reduce((a, b) => (b.v < a.v ? b : a));
        if (lo.v <= 0) return;
        const change = (hi.v - lo.v) / lo.v;
        const meaningful = metric === 'queue' ? hi.v - lo.v >= 2 : hi.v - lo.v >= 50;
        if (meaningful && change >= 0.25 && (!best || change > best.change)) best = { r, hi, lo, change, fmt, noun };
      });
    });
    if (best) {
      insights.push({
        key: 'swing',
        tone: 'info',
        icon: 'graph-up-arrow',
        score: 55,
        label: 'Biggest change',
        text: (
          <>
            <strong>{best.r.subject.name}</strong>'s {best.noun} went from <strong>{best.fmt(best.lo.v)}</strong> ({best.lo.g.label.short})
            {' '}to <strong>{best.fmt(best.hi.v)}</strong> ({best.hi.g.label.short}), <strong>{Math.round(best.change * 100)}%</strong> higher.
          </>
        ),
      });
    }
  }

  // Slowest transactions
  const withTx = rows.filter((r) => r.overall?.transaction?.avg > 0).sort((a, b) => b.overall.transaction.avg - a.overall.transaction.avg);
  if (withTx.length >= 2) {
    const slow = withTx[0];
    const fast = withTx[withTx.length - 1];
    insights.push({
      key: 'tx',
      tone: 'info',
      icon: 'stopwatch',
      score: 40,
      label: 'Transactions',
      text: (
        <>
          <strong>{slow.subject.name}</strong> had the slowest transactions ({formatSeconds(slow.overall.transaction.avg)} on average);
          {' '}<strong>{fast.subject.name}</strong> the quickest ({formatSeconds(fast.overall.transaction.avg)}).
        </>
      ),
    });
  }

  return insights.sort((a, b) => b.score - a.score).slice(0, 5);
};
